import React from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowUpRightIcon } from "lucide-react";
import { Header } from "../Header";
import { images, eventDate } from "../../data/site";
import { useCountdown } from "../../hooks/useCountdown";
export function Hero() {
  const {
    days
  } = useCountdown(eventDate);
  const reduce = useReducedMotion();
  const enter = (delay: number) => reduce ? {} : {
    initial: {
      opacity: 0,
      y: 16
    },
    animate: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 0.3,
      delay,
      ease: [0.23, 1, 0.32, 1]
    }
  };
  return <section className="p-3 md:p-5" data-id="element-77" data-mp="qm7g18an41r">
      <div className="relative overflow-hidden rounded-[28px] md:rounded-[36px]" data-id="element-78" data-mp="3rwx7pv5hc0s1">
        <img src={images.hero} alt="Un parapente orange survole la vallée de Mieussy et le village, Mont-Blanc en arrière-plan" className="absolute inset-0 h-full w-full object-cover" data-id="element-79" data-mp="1v4eteu7q0lqn" />
        <div className="absolute inset-0 bg-navy/35" aria-hidden="true" data-id="element-80" data-mp="1q37pj68p86p9" />

        <div className="relative mx-auto flex min-h-[880px] max-w-[1320px] flex-col px-4 pb-6 pt-4 md:px-10 md:pt-6 lg:min-h-[940px]" data-id="element-81" data-mp="1muricp2n863z">
          <Header data-id="element-82" data-mp="1cy6bdy72ckxi" />

          <motion.div {...enter(0)} className="mt-10 inline-flex w-fit items-center gap-4 rounded-full bg-cream/95 py-2 pl-2 pr-6 md:mt-14" data-id="element-83" data-mp="1cyoe9do8dxee">
            <span className="rounded-full bg-navy px-4 py-2 font-serif text-2xl leading-none text-cream" data-id="element-84" data-mp="1hxrjoz84i5mw">J-{days}</span>
            <span className="text-sm leading-snug text-navy" data-id="element-85" data-mp="25owydb2burvm">
              Avant le cinquantenaire
              <span className="block font-medium uppercase tracking-wide" data-id="element-86" data-mp="2anrhyf84ckgs">23 — 25 juin 2028</span>
            </span>
          </motion.div>

          <motion.h1 {...enter(0.06)} className="mt-8 max-w-3xl font-serif text-[56px] leading-[1.02] text-cream md:text-[96px] lg:text-[112px]" data-id="element-87" data-mp="20vbzzhwtnvvw">
            50 ans de vol, <span className="italic" data-id="element-88" data-mp="2ojv8nyjsaaoi">né ici.</span>
          </motion.h1>

          <motion.p {...enter(0.12)} className="mt-6 max-w-xl text-lg leading-relaxed text-cream md:text-xl" data-id="element-89" data-mp="2tdzf8gp50gp2">
            Le 25 juin 1978, depuis le Pertuiset, le premier « parachute de pente » prenait son envol. En juin 2028, Mieussy fête le demi-siècle du parapente.
          </motion.p>

          <motion.div {...enter(0.18)} className="mt-9 flex flex-wrap gap-3" data-id="element-90" data-mp="2u6dzqsx72e2l">
            <Link to="/programme" className="group inline-flex items-center gap-3 rounded-full bg-red py-2 pl-6 pr-2 font-semibold text-white transition-colors duration-150 hover:bg-red-deep" data-id="element-91" data-mp="2p9f6eclcub5g">
              Découvrir le programme
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-red transition-transform duration-150 group-hover:rotate-45" data-id="element-92" data-mp="2fhhqisjeiv0e">
                <ArrowUpRightIcon className="h-4 w-4" data-id="element-93" data-mp="26ixf8cg67nm7" />
              </span>
            </Link>
            <Link to="/histoire" className="inline-flex items-center rounded-full bg-cream px-7 py-3.5 font-semibold text-navy transition-colors duration-150 hover:bg-sand" data-id="element-94" data-mp="21jkupycrusms">
              L'histoire du premier vol
            </Link>
          </motion.div>

          <div className="mt-auto flex justify-end pt-12" data-id="element-95" data-mp="c40attyft4hy">
            <motion.div {...enter(0.24)} className="w-full max-w-[620px] rounded-3xl bg-cream p-4 md:p-5" data-id="element-96" data-mp="gzxua94zyqyy">
              <div className="flex items-baseline justify-between px-1" data-id="element-97" data-mp="m1er9tkd1ta9">
                <p className="text-sm font-medium text-navy" data-id="element-98" data-mp="q1yosy8uiywx">Le berceau mondial du parapente</p>
                <p className="text-sm text-navy-soft" data-id="element-99" data-mp="3phc121em0z82">Mieussy, Haute-Savoie</p>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2" data-id="element-100" data-mp="3ufdmuu3f7cn8">
                <figure className="relative overflow-hidden rounded-2xl" data-id="element-101" data-mp="3udso0sa2p7jk">
                  <img src={images.archive1978} alt="Photo d'archive : les pionniers préparent leur voile en 1978" className="aspect-[4/3] w-full object-cover" data-id="element-102" data-mp="2ajjqxpoq3hz" />
                  <figcaption className="absolute bottom-2 left-2 rounded-full bg-cream/95 px-3 py-1 text-xs font-medium text-navy" data-id="element-103" data-mp="36qub24l5whqe">1978 · Les pionniers</figcaption>
                </figure>
                <figure className="relative overflow-hidden rounded-2xl" data-id="element-104" data-mp="2rn1aox4aa5n6">
                  <img src={images.pertuiset} alt="Un pilote gonfle sa voile au décollage du Pertuiset" className="aspect-[4/3] w-full object-cover" data-id="element-105" data-mp="2rkyc3op2f46u" />
                  <figcaption className="absolute bottom-2 left-2 rounded-full bg-cream/95 px-3 py-1 text-xs font-medium text-navy" data-id="element-106" data-mp="31e5q1xxvfsij">Le Pertuiset · aujourd'hui</figcaption>
                </figure>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}